<template>
  <FormGroup>
    <Text variant="body-large" color="secondary">Choose a day and time to suggest to the renters</Text>

    <FormField :error="form.fieldErrors('date')">
      <LineDatePicker v-model="form.fields.date" />
    </FormField>

    <FormField label="Time" :error="form.fieldErrors('time')">
      <Select v-model="form.fields.time">
        <option v-for="time in timeOptions" :key="time">{{ time }}</option>
      </Select>
    </FormField>

    <FormField :label="agentCopy.fieldLabel" :error="form.fieldErrors('showingAgentId')">
      <ShowingAgentPicker v-model="form.fields.showingAgentId" :property-id="propertyId" />

      <!--
        A sibling Text rather than FormField's `hint`, which is hidden while the field has an error.
        The reason step below is gated on this field, so a self-guided owner stuck here needs the
        clarifier most once validation has fired.
      -->
      <Text v-if="agentCopy.fieldHint" variant="body-small" color="secondary">
        {{ agentCopy.fieldHint }}
      </Text>
    </FormField>

    <Transition name="reason-reveal">
      <div v-if="isScheduleStepComplete" class="reason-section">
        <FormField :error="form.fieldErrors('reasonType')">
          <View justify="space-between" align="center" gap="sm">
            <Label>Why are you rescheduling?</Label>
            <Badge size="small" variant="grey" icon="lock-01" label="Just for Tallo" />
          </View>
          <Text variant="body-small" color="secondary">
            Tallo uses this to propose times that actually work - less back-and-forth.
          </Text>
          <RadioButtonGroup direction="column">
            <RadioCard
              v-for="option in reasonOptions"
              :key="option.value"
              v-model="form.fields.reasonType"
              name="rescheduleReason"
              :value="option.value"
            >
              <View gap="md" align="center">
                <IconTile :icon="option.icon" size="small" color="neutral" :colored-icon="false" />
                <View direction="column">
                  <Text variant="label-medium">{{ option.title }}</Text>
                  <Text variant="body-small" color="secondary">{{ option.description }}</Text>
                </View>
              </View>
            </RadioCard>
          </RadioButtonGroup>
        </FormField>

        <FormField v-if="isCantShow" label="For how long?">
          <Chips>
            <Chip
              v-for="option in spanOptions"
              :key="option.value"
              type="button"
              :is-active="form.fields.unavailabilitySpan === option.value"
              @click="form.fields.unavailabilitySpan = option.value"
            >
              {{ option.title }}
            </Chip>
          </Chips>
        </FormField>

        <View v-if="isUnavailabilityRange" gap="md" class="date-range">
          <FormField label="Unavailable from" :error="form.fieldErrors('unavailableFrom')" class="date-range-field">
            <Input type="date" v-model="form.fields.unavailableFrom" />
          </FormField>
          <FormField label="Back on" :error="form.fieldErrors('unavailableUntil')" class="date-range-field">
            <Input type="date" v-model="form.fields.unavailableUntil" />
          </FormField>
        </View>

        <FormField v-if="isPropertyNotReady" label="Ready to show from" :error="form.fieldErrors('notReadyUntil')">
          <Input type="date" v-model="form.fields.notReadyUntil" />
        </FormField>

        <Transition name="effect" mode="out-in">
          <div v-if="effectText" :key="effectText" class="effect-hint">
            <span class="effect-dot" />
            <div class="effect-body">
              <span class="effect-eyebrow">How Tallo adapts</span>
              <Text variant="body-small">{{ effectText }}</Text>
            </div>
          </div>
        </Transition>

        <FormField
          :id="commentFieldId"
          hint="Optional — Tallo works it into the renters’ message."
          :error="form.fieldErrors('comment')"
        >
          <View justify="space-between" align="center" gap="sm">
            <Label :for="commentFieldId">Leave a comment for the renters</Label>
            <Badge size="small" variant="blue" icon="message-chat-square" label="Shared with renters" />
          </View>
          <Textarea rows="3" v-model="form.fields.comment" />
        </FormField>
      </div>
    </Transition>
  </FormGroup>
</template>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';
import { computed, UnwrapNestedRefs, useId, watch } from 'vue';

import {
  Badge,
  Chip,
  Chips,
  FormComposable,
  FormField,
  FormGroup,
  IconName,
  IconTile,
  Input,
  Label,
  LineDatePicker,
  RadioButtonGroup,
  RadioCard,
  Select,
  Text,
  Textarea,
  View,
} from '@tallo/design-system';
import { ShowingAgentPicker } from '@tallo/investor/showing-agents';

import { RescheduleReasonType, RescheduleUnavailabilitySpan } from '../interfaces/reschedule-reason.interface';
import { TourType } from '../interfaces/tour-type.enum';
import { timeOptions } from '../time-options.const';
import { RescheduleShowingFormModel } from '../utils/reschedule-showing-form.util';
import { getShowingAgentCopy } from '../utils/showing-agent-copy.util';

const props = defineProps<{
  /** Start time of the showing being rescheduled — used to name the old slot in the hint copy. */
  currentStartTime?: Date | string;
  /** Optional: omitted, the agent field keeps its in-person framing. */
  tourType?: TourType;
  /** The unit being toured, whose own agents the picker offers */
  propertyId?: string;
}>();

const agentCopy = computed(() => getShowingAgentCopy(props.tourType));

const form = defineModel<UnwrapNestedRefs<FormComposable<RescheduleShowingFormModel>>>('form', { required: true });

const commentFieldId = useId();

const reasonOptions: Array<{
  value: RescheduleReasonType;
  title: string;
  description: string;
  icon: IconName;
}> = [
  {
    value: RescheduleReasonType.CANT_SHOW,
    title: 'Can’t show at that time',
    description: 'Away, busy, sick, or bad weather',
    icon: 'calendar-x-02',
  },
  {
    value: RescheduleReasonType.PROPERTY_NOT_READY,
    title: 'The property isn’t ready to show',
    description: 'Cleaning, repairs, rekeying, tenant still moving out',
    icon: 'home-03',
  },
  {
    value: RescheduleReasonType.BOOKING_CONFLICT,
    title: 'It conflicts with another booking',
    description: 'Another showing or appointment at that time',
    icon: 'copy',
  },
  {
    value: RescheduleReasonType.OTHER,
    title: 'Something else',
    description: 'None of the above',
    icon: 'dots-horizontal',
  },
];

const spanOptions: Array<{ value: RescheduleUnavailabilitySpan; title: string }> = [
  { value: RescheduleUnavailabilitySpan.SLOT, title: 'Just that time' },
  { value: RescheduleUnavailabilitySpan.DAY, title: 'That whole day' },
  { value: RescheduleUnavailabilitySpan.RANGE, title: 'Several days' },
];

/** The reason step only appears once the scheduling step is complete: date, time, and host picked. */
const isScheduleStepComplete = computed(
  () => Boolean(form.value.fields.date) && Boolean(form.value.fields.time) && Boolean(form.value.fields.showingAgentId),
);

const isCantShow = computed(() => form.value.fields.reasonType === RescheduleReasonType.CANT_SHOW);
const isPropertyNotReady = computed(() => form.value.fields.reasonType === RescheduleReasonType.PROPERTY_NOT_READY);
const isUnavailabilityRange = computed(
  () => isCantShow.value && form.value.fields.unavailabilitySpan === RescheduleUnavailabilitySpan.RANGE,
);

watch(isUnavailabilityRange, (isRange) => {
  if (isRange && !form.value.fields.unavailableFrom && props.currentStartTime) {
    form.value.fields.unavailableFrom = useDateFormat(props.currentStartTime, 'YYYY-MM-DD').value;
  }
});

// The new showing date doubles as the owner's own estimate of when the property is ready — offer
// it as the default, but never clobber a date they already typed.
watch(isPropertyNotReady, (isNotReady) => {
  if (isNotReady && !form.value.fields.notReadyUntil && form.value.fields.date) {
    form.value.fields.notReadyUntil = useDateFormat(form.value.fields.date, 'YYYY-MM-DD').value;
  }
});

const slotLabel = computed(() => {
  if (!props.currentStartTime) {
    return null;
  }

  return new Date(props.currentStartTime).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
});

const dayLabel = computed(() => {
  if (!props.currentStartTime) {
    return null;
  }

  return new Date(props.currentStartTime).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
});

// Full weekday name ("Thursday") for the "not every Thursday" clarification in the hints — owners
// otherwise read a one-off block as repeating weekly.
const weekdayLabel = computed(() => {
  if (!props.currentStartTime) {
    return null;
  }

  return new Date(props.currentStartTime).toLocaleDateString('en-US', { weekday: 'long' });
});

function formatDateField(value: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

const effectText = computed(() => {
  const { reasonType, unavailabilitySpan, unavailableFrom, unavailableUntil, notReadyUntil } = form.value.fields;

  switch (reasonType) {
    case RescheduleReasonType.CANT_SHOW:
      if (unavailabilitySpan === RescheduleUnavailabilitySpan.DAY) {
        return `Tallo won’t propose showing times on ${dayLabel.value ?? 'the original showing day'} at any of your properties — other days stay open.`;
      }

      if (unavailabilitySpan === RescheduleUnavailabilitySpan.RANGE) {
        return unavailableFrom && unavailableUntil
          ? `Tallo won’t propose showing times from ${formatDateField(unavailableFrom)} to ${formatDateField(unavailableUntil)} at any of your properties. Times outside that window stay open.`
          : 'Tallo won’t propose showing times in that window at any of your properties. Times outside it stay open.';
      }

      return `Tallo will stop offering ${slotLabel.value ?? 'the original time'} to every renter — everything else stays open.`;
    case RescheduleReasonType.PROPERTY_NOT_READY:
      return notReadyUntil
        ? `Tallo won’t propose tours at this property before ${formatDateField(notReadyUntil)}.`
        : 'Tallo won’t propose tours at this property until it’s ready.';
    case RescheduleReasonType.BOOKING_CONFLICT:
      return `Tallo will stop offering the old time to every renter — just that one time, not every ${weekdayLabel.value ?? 'week'}. Your other times stay open.`;
    case RescheduleReasonType.OTHER:
      return `Tallo will stop offering the old time to every renter — just that one time, not every ${weekdayLabel.value ?? 'week'}. Add a comment and it will pick up any constraint you mention.`;
    default:
      return null;
  }
});
</script>

<style scoped lang="scss">
.date-range {
  width: 100%;
}

.date-range-field {
  flex: 1;
  min-width: 0;
}

.effect-hint {
  display: flex;
  gap: var(--t-spacing-sm);
  align-items: flex-start;
  padding: var(--t-spacing-md);
  border-radius: var(--t-border-radius-md);
  background: var(--t-color-violet-100);
}

.effect-dot {
  flex-shrink: 0;
  width: 0.5rem;
  height: 0.5rem;
  margin-top: 0.375rem;
  border-radius: var(--t-border-radius-full-rounded);
  background: var(--t-color-violet-600);
}

.effect-body {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.effect-eyebrow {
  font: var(--t-typography-label-sm-font);
  font-size: 0.6875rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--t-color-violet-600);
}

.effect-enter-active,
.effect-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.effect-enter-from,
.effect-leave-to {
  opacity: 0;
  transform: translateY(0.375rem);
}

// The wrapper replaces the FormGroup as the reason fields' parent, so it repeats the group's
// column layout and (large) gap — without it the revealed fields would collapse onto each other.
.reason-section {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-lg);
}

.reason-reveal-enter-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.reason-reveal-enter-from {
  opacity: 0;
  transform: translateY(0.5rem);
}

@media (prefers-reduced-motion: reduce) {
  .effect-enter-active,
  .effect-leave-active,
  .reason-reveal-enter-active {
    transition: none;
  }
}
</style>
